import URI from 'jsuri';
import Q from 'q';

import API from 'js/lib/api';
import { getIetfLanguageTag } from 'js/lib/language';
import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import user from 'js/lib/user';

import { LABS_HOSTNAME } from 'bundles/labs-common/constants/LabUrls';
import type {
  ExpiresAtResponse,
  GetLabSandboxLaunchUrlResponse,
  GetWorkspaceExpirationResponse,
  GetWorkspaceResponse,
  LaunchModeType,
  LearnerWorkspaceLaunchResponse,
  WorkspaceHealthChecksResponse,
  WorkspaceLaunchDefinitionsResponse,
  WorkspaceLaunchUrlResponse,
  WorkspaceMetadataResponse,
  WorkspaceTemplateResponse,
  WorkspaceV1LaunchResponse,
  WorkspaceV1Launcher,
  WorkspaceV2Launcher,
} from 'bundles/labs-common/types/WorkspaceApiTypes';
import type { WorkspaceVersionChangeSummaries } from 'bundles/launched-lab/types/WorkspaceVersionChange';
import type { NaptimeError } from 'bundles/naptimejs';

const onDemandWorkspaceLaunchersV1Api = API('/api/onDemandWorkspaceLaunchers.v1', { type: 'rest' });
const onDemandLearnerWorkspacesApi = API('/api/onDemandLearnerWorkspaces.v1', { type: 'rest' });
const onDemandWorkspacePublishApi = API('/api/onDemandWorkspacePublish.v1', { type: 'rest' });
const workspaceLaunchApi = API('/api/workspaceLaunch.v1', { type: 'rest' });
const workspaceUserSessionsApi = API('/api/workspaceUserSessions.v1', { type: 'rest' });
const workspaceLaunchDefinitionsApi = (workspaceId: string) =>
  API(`/api/grpc/labscore/v1beta1/LabLaunchDefinitionAPI/Get/${workspaceId}`, {
    type: 'rest',
  });
const workspaceAdminHostDockerImagesApi = (devStackName: string) =>
  API(`https://hub.${devStackName}.labs-dev.coursera.org/api/workspaceAdminHostDockerImages.v1`, {
    type: 'rest',
  });
const workspaceMetadataApi = (workspaceId: string) => API(`/api/workspaceMetadata.v1/${workspaceId}`, { type: 'rest' });
const workspaceVersionUpdateSummariesApi = API('/api/workspaceVersionUpdateSummaries.v1', { type: 'rest' });
const workspaceTemplatesApi = API('/api/workspaceTemplates.v1', { type: 'rest' });

const WORKSPACE_FIELDS = [
  'description',
  'launchPath',
  'launcherLabel',
  'offlineInstructions',
  'workspaceId',
  'shareDetails',
  'templateId',
].join(',');

const WORKSPACE_V2_FIELDS = [
  'id',
  'userId',
  'courseId',
  'itemId',
  'templateId',
  'isStandardSubmission',
  'isFileBrowserEnabled',
  'description',
  'offlineInstructions',
  'sharedWorkspaceUrl',
  'label',
  'shareConfig',
  'inLabInstructionsParts',
  'contentPath',
  'imageApplicationType',
  'versionedAtomId',
  'workspaceLauncherId',
].join(',');

const GENERIC_WORKSPACE_FIELDS = ['workspaceId', 'expiresAt', 'isHealthy', 'url'].join(',');

const WORKSPACE_METADATA_FIELDS = ['accessScopes', 'publishDestinations', 'owners', 'templateId'].join(',');

const WORKSPACE_VERSION_CHANGE_SUMMARIES_FIELDS = 'summaries';

export const getWorkspaceV1Launcher = (courseId: string, itemId: string): Q.Promise<WorkspaceV1Launcher> => {
  const id = tupleToStringKey([user.get().id, courseId, itemId]);
  const uri = new URI(id).addQueryParam('fields', WORKSPACE_FIELDS);

  // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  return Q(onDemandWorkspaceLaunchersV1Api.get(uri.toString())).then(
    (response: { elements: Array<WorkspaceV1Launcher> }) => response.elements[0]
  );
};

export const getWorkspaceV2Launcher = async (
  courseId: string,
  itemId: string,
  programId?: string
): Promise<WorkspaceV2Launcher> => {
  const requestHeaders: HeadersInit = new Headers();
  requestHeaders.set('Accept-Language', getIetfLanguageTag());
  const id = tupleToStringKey([user.get().id, courseId, itemId]);
  let urlToFetch = `/api/onDemandWorkspaceLaunchers.v2/${id}?fields=${WORKSPACE_V2_FIELDS}`;
  if (programId) {
    urlToFetch += `&programId=${programId}`;
  }
  const response = await fetch(urlToFetch, {
    headers: requestHeaders,
  });

  if (!response.ok) {
    throw Object.assign(new Error(`Error fetching workspace launcher: ${response.statusText}`), {
      status: response.status,
    });
  }

  const responseJson: { elements: Array<WorkspaceV2Launcher> } = await response.json();
  const launcher = responseJson.elements[0];
  if (!launcher) {
    throw new Error('Workspace launcher response did not include a launcher');
  }
  return launcher;
};

export const getExpiresAt = (courseId: string, itemId: string): Q.Promise<ExpiresAtResponse> => {
  const id = tupleToStringKey([user.get().id, courseId, itemId]);
  const uri = new URI(id).addQueryParam('fields', 'expiresAt');
  return Q(onDemandLearnerWorkspacesApi.get(uri.toString()));
};

export const extendSession = (courseId: string, itemId: string): Q.Promise<number> => {
  const id = tupleToStringKey([user.get().id, courseId, itemId]);
  const uri = new URI().addQueryParam('action', 'extend').addQueryParam('id', id);
  return Q(onDemandLearnerWorkspacesApi.post(uri.toString()));
};

export const launchV1Workspace = (courseId: string, itemId: string): Q.Promise<WorkspaceV1LaunchResponse> => {
  const id = tupleToStringKey([user.get().id, courseId, itemId]);
  const uri = new URI().addQueryParam('action', 'launch').addQueryParam('id', id);

  return Q(onDemandWorkspaceLaunchersV1Api.post(uri.toString()));
};

/**
 * It can be useful to know whether a lab has been launched (which creates its workspace),
 * because some APIs will fail if called on a workspace that has not yet been created.
 */
export const getHasLabBeenLaunched = async (courseId: string, itemId: string) => {
  const id = tupleToStringKey([user.get().id, courseId, itemId]);
  const uri = new URI(id).addQueryParam('fields', 'launchUrl');

  // This API will return 404 if the lab has never been launched.
  try {
    await onDemandLearnerWorkspacesApi.get(uri.toString());
    return true;
  } catch (error: unknown) {
    if (error && (error as NaptimeError).status === 404) {
      return false;
    }
  }

  return true;
};

export const launchLearnerWorkspace = (
  courseId: string,
  itemId: string,
  learnerId?: string,
  programId?: string,
  launchMode?: LaunchModeType
): Q.Promise<LearnerWorkspaceLaunchResponse> => {
  const id = tupleToStringKey([learnerId || user.get().id, courseId, itemId]);
  const uri = new URI().addQueryParam('action', 'launch').addQueryParam('id', id);
  if (programId) {
    uri.addQueryParam('programId', programId);
  }
  const options = launchMode ? { data: { launchMode } } : undefined;
  return Q(onDemandLearnerWorkspacesApi.post(uri.toString(), options));
};

export const getLabSandboxLaunchUrl = (
  courseId: string,
  instructorWorkspaceId: string,
  launchPath?: string | undefined
): Q.Promise<GetLabSandboxLaunchUrlResponse> => {
  const uri = new URI()
    .addQueryParam('action', 'launchInSandbox')
    .addQueryParam('courseId', courseId)
    .addQueryParam('instructorWorkspaceId', instructorWorkspaceId)
    .addQueryParam('isIframe', 'true');

  if (launchPath) {
    uri.addQueryParam('path', launchPath);
  }

  return Q(onDemandLearnerWorkspacesApi.post(uri.toString()));
};

export const refreshAccessToken = (workspaceId: string, userId?: number): Q.Promise<string> => {
  const id = tupleToStringKey([userId || user.get().id, workspaceId]);
  const uri = new URI().addQueryParam('action', 'refreshAccessToken').addQueryParam('id', id);

  return Q(workspaceUserSessionsApi.post(uri.toString()));
};

export const getWorkspace = (workspaceId: string, maybeUserId?: number): Q.Promise<GetWorkspaceResponse> => {
  const userId = maybeUserId || user.get().id;
  const userWorkspaceId = tupleToStringKey([userId, workspaceId]);
  const uri = new URI(userWorkspaceId).addQueryParam('fields', GENERIC_WORKSPACE_FIELDS);

  return Q(workspaceUserSessionsApi.get(uri.toString())).then((response) => {
    return response.elements[0];
  });
};

export const getWorkspaceExpiration = async (
  workspaceId: string,
  maybeUserId?: number
): Promise<GetWorkspaceExpirationResponse> => {
  const userId = maybeUserId || user.get().id;
  const userWorkspaceId = tupleToStringKey([userId, workspaceId]);
  const uri = new URI(userWorkspaceId)
    // Explicitly requesting and extracting `expiresAt` because `expiresAt` is not exposed by default.
    .addQueryParam('fields', 'expiresAt');

  const response = await fetch('/api/workspaceUserSessions.v1/' + uri.toString());
  const json: ExpiresAtResponse = await response.json();

  const maybeCurrentDateTime = response.headers.get('Date');
  const currentTime = maybeCurrentDateTime ? new Date(maybeCurrentDateTime).getTime() : Date.now();

  return {
    expiresAt: json.elements[0]?.expiresAt,
    currentTime,
  };
};

export const extendWorkspaceSession = (workspaceId: string, maybeUserId?: number): Q.Promise<number> => {
  const userId = maybeUserId || user.get().id;
  const userWorkspaceId = tupleToStringKey([userId, workspaceId]);
  const uri = new URI().addQueryParam('action', 'extend').addQueryParam('id', userWorkspaceId);

  return Q(workspaceUserSessionsApi.post(uri.toString()));
};

export const publishWorkspace = (courseId: string, itemId: string, async = false): Q.Promise<{ id: string }> => {
  const id = tupleToStringKey([user.get().id, courseId, itemId]);
  const uri = new URI()
    .addQueryParam('action', !async ? 'publish' : 'asyncPublish')
    .addQueryParam('id', id)
    .addQueryParam('fields', 'id');

  const options = {
    data: {
      shareSettings: {
        accessScopes: [
          {
            typeName: 'loggedInScope',
            definition: {},
          },
        ],
      },
    },
  };

  return Q(onDemandWorkspacePublishApi.post(uri.toString(), options));
};

export const getWorkspaceLaunchUrl = (
  workspaceId: string,
  path?: string,
  forceRefresh?: boolean,
  isIframe?: boolean
): Q.Promise<WorkspaceLaunchUrlResponse> => {
  const id = tupleToStringKey([user.get().id, workspaceId]);
  const uri = new URI().addQueryParam('action', 'launch').addQueryParam('userWorkspaceId', id);

  if (typeof path === 'string') {
    uri.addQueryParam('urlPath', path); // TODO(riwong): Remove this when backend WorkspaceLaunchResource migrates to `path`
    uri.addQueryParam('path', path);
  }

  if (typeof forceRefresh === 'boolean') {
    uri.addQueryParam('forceRefresh', forceRefresh.toString());
  }

  if (typeof isIframe === 'boolean') {
    uri.addQueryParam('isIframe', isIframe.toString());
  }

  return Q(workspaceLaunchApi.post(uri.toString()));
};

export const getWorkspaceImageTag = (workspaceId: string, jwtToken: string): Q.Promise<string> => {
  const uri = new URI().addQueryParam('fields', 'imageTag');
  return Q(
    workspaceLaunchDefinitionsApi(workspaceId).get(uri.toString(), {
      headers: { 'X-Coursera-JWT-Auth': jwtToken },
    })
  ).then((response: { body: { imageTag: string } }) => {
    return response.body.imageTag;
  });
};

export const pullWorkspaceAdminHostDockerImages = (
  devStackName: string,
  imageTag: string,
  jwtToken: string
): Q.Promise<void> => {
  const uri = new URI().addQueryParam('action', 'pull');

  return Q(
    workspaceAdminHostDockerImagesApi(devStackName).post(uri.toString(), {
      headers: {
        'X-Coursera-JWT-Auth': jwtToken,
      },
      data: {
        repositoryNames: [imageTag],
      },
    })
  );
};

export const getWorkspaceHealthCheck = (
  workspaceId: string,
  hostname: string
): Promise<WorkspaceHealthChecksResponse | undefined> => {
  return fetch(
    `https://${hostname}/api/workspaceHealthChecks.v1/${workspaceId}?fields=isHealthy,url,workspaceStatusType`,
    {
      // Need workspaceId-wkspc-sid cookie to be sent with this cross-origin request
      credentials: 'include',
    }
  )
    .then((response) => response.json())
    .then((response) => response.elements?.[0]);
};

export const getWorkspaceMetadata = (workspaceId: string): Q.Promise<WorkspaceMetadataResponse> => {
  const uri = new URI().addQueryParam('fields', WORKSPACE_METADATA_FIELDS);

  return Q(workspaceMetadataApi(workspaceId).get(uri.toString())).then((response) => response.elements[0]);
};

export const getWorkspaceLaunchDefinitions = (
  workspaceId: string,
  jwt: string
): Q.Promise<WorkspaceLaunchDefinitionsResponse> => {
  return Q(
    workspaceLaunchDefinitionsApi(workspaceId).get(new URI().toString(), {
      headers: {
        'X-Coursera-JWT-Auth': jwt,
        'X-Coursera-Naptime-Fields': 'all',
      },
    })
  ).then((response) => response.body);
};

export const getWorkspaceTemplates = (templateId: string): Q.Promise<WorkspaceTemplateResponse> => {
  const uri = new URI(templateId);
  return Q(
    workspaceTemplatesApi.get(uri.toString(), {
      headers: { 'X-Coursera-Naptime-Fields': 'all' },
    })
  ).then((response) => response.elements[0]);
};

export const markAsViewedWorkspaceVersionChangeSummaries = (workspaceId: string): Q.Promise<void> => {
  const uri = new URI().addQueryParam('action', 'markAsViewed').addQueryParam('id', workspaceId);
  return Q(workspaceVersionUpdateSummariesApi.post(uri.toString()));
};

export const getUnviewedWorkspaceVersionChangeSummaries = (
  workspaceId: string
): Q.Promise<WorkspaceVersionChangeSummaries> => {
  const uri = new URI()
    .addQueryParam('q', 'getNotViewed')
    .addQueryParam('id', workspaceId)
    .addQueryParam('fields', WORKSPACE_VERSION_CHANGE_SUMMARIES_FIELDS);
  return Q(workspaceVersionUpdateSummariesApi.get(uri.toString()).then((response) => response.elements[0]));
};

export const getAllWorkspaceVersionChangeSummaries = (
  workspaceId: string
): Q.Promise<WorkspaceVersionChangeSummaries> => {
  const uri = new URI(workspaceId).addQueryParam('fields', WORKSPACE_VERSION_CHANGE_SUMMARIES_FIELDS);
  return Q(workspaceVersionUpdateSummariesApi.get(uri.toString()).then((response) => response.elements[0]));
};

export const saveLabEnvironment = (
  courseId: string,
  workspaceId: string,
  jwtToken: string,
  versionNotes: string,
  workspaceGatewayHost?: string
): Promise<{ successful: {} }> => {
  const host = workspaceGatewayHost || LABS_HOSTNAME;
  const uri = new URI(`https://${host}/api/workspaceImages.v1`)
    .addQueryParam('action', 'commit')
    .addQueryParam('courseId', courseId)
    .addQueryParam('workspaceId', workspaceId);

  const body = {
    ...(versionNotes ? { versionNotes } : {}),
  };

  return fetch(uri.toString(), {
    method: 'POST',
    headers: {
      'X-Coursera-JWT-Auth': jwtToken,
    },
    body: JSON.stringify(body),
  }).then((response) => response.json());
};

export const parseVersionedAtomId = (versionedAtomId: string): { atomId: string; version?: number } => {
  const parts = versionedAtomId.split('@');
  const atomId = parts[0] ?? versionedAtomId;
  const versionString = parts[1];
  const version = versionString ? parseInt(versionString, 10) : undefined;
  return { atomId, version };
};
